import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ActionButtons from './ActionButtons';
import InlineImproveInput from '../common/InlineImproveInput';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import { Accordion } from '../atoms';
import MarkdownRenderer from '../common/MarkdownRenderer';

/**
 * Individual Process Flow Diagram Item Component
 * Renders a single diagram in an accordion with actions
 */
const ProcessFlowDiagramItem = ({
  diagram,
  index,
  isExpanded,
  isHeightExpanded,
  isValidated,
  memberType,
  isAssignedValidator,
  zoomLevel,
  showImproveInput,
  improvePrompt,
  onToggle,
  onToggleHeight,
  onOpenPopup,
  onValidate,
  onEdit,
  onImprove,
  onExplain,
  onCopy,
  onDelete,
  onZoomIn,
  onZoomOut,
  onImprovePromptChange,
  onSendImprove,
  onCancelImprove,
  registerSectionRef,
  currentVersion,
  currentVersionIndex,
  showNotification,
  onComment,
  commentCount = 0
}) => {
  const diagramId = diagram.id;
  const versionSpecificId = `${diagramId}_vt${currentVersion?.timestamp || currentVersionIndex}`;

  return (
    <Accordion
      ref={(el) => registerSectionRef(diagramId, el)}
      title={diagram.title}
      defaultExpanded={false}
      expanded={isExpanded}
      onChange={(_, expanded) => onToggle(diagramId)}
      rightIcon={
        <Box
          onClick={async (e) => {
            e.stopPropagation();

            try {
              await onValidate(diagramId);
            } catch (error) {
              showNotification({
                message: `Failed to update validation for "${diagram.title}"`,
                type: "error"
              });
            }
          }}
          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
        >
          {isValidated ? (
            <ValidatedIcon size={25} memberType={memberType} />
          ) : (
            <UnvalidatedIcon size={25} />
          )}
        </Box>
      }
      detailsContent={
        <>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '93%',
            textAlign: 'justify',
            minHeight: "160px",
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              marginTop: '5px',
              marginBottom: '5px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.4)',
              },
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: 'transparent',
            },
          }}
          >
            {/* Description shown before the diagram */}
            {diagram.descriptionBefore && (
              <Box
                data-description="before"
                sx={{
                  mb: 2,
                  fontStyle: 'normal',
                  backgroundColor: 'transparent',
                  p: 1,
                  pl: 0,
                  color: 'text.primary',
                  minHeight: 'auto',
                  height: 'auto'
                }}
              >
                <Box sx={{ fontSize: "14px", '& p': { mb: 0.5 }, '& ul, & ol': { mt: 0, mb: 0.5, pl: 2 }, '& li': { mb: 0.25 } }}>
                  <MarkdownRenderer content={diagram.descriptionBefore} />
                </Box>
              </Box>
            )}
            {/* Mermaid diagram */}
            {isExpanded && <Box
              className="accordion-diagram"
              data-diagram-id={diagram.id}
              sx={{
                width: "100%",
                position: 'relative',
                minHeight: '300px',
                // maxHeight: isHeightExpanded ? 'none' : 'calc(100vh - 500px)',
                height: 'auto',
                pt: 1,
                pb: 2,
                mb: 2,
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                transition: 'max-height 0.3s ease',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  },
                },
              }}
            >
              <Box
                sx={{
                  zoom: zoomLevel,
                  minWidth: '100%',
                  width: 'max-content',
                  height: 'auto',
                  padding: 2,
                  '& svg': {
                    minWidth: '600px',
                    height: 'auto',
                    display: 'block',
                    overflow: 'visible',
                    '& text': {
                      fontFamily: '"Arial", sans-serif',
                      fontSize: '14px !important',
                      textRendering: 'optimizeLegibility',
                      wordBreak: 'break-word',
                    },
                    '& .node rect, & .node polygon, & .node circle, & .node ellipse': {
                      overflow: 'visible',
                    },
                  }
                }}
              >
                <div
                  id={diagram.id}
                  className="mermaid-diagram"
                  data-code={diagram.code}
                  data-processed="false"
                  style={{
                    minHeight: (diagram.code || '').includes("Error") ? "200px" : "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 1.5, minHeight: '200px' }}>
                    <CircularProgress sx={{
                      color: '#475969',
                      width: "20px !important", height: "20px !important", minWidth: '20px',
                      '& svg': {
                        width: "20px", height: "20px", minWidth: '20px'
                      }
                    }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading diagram...
                    </Typography>
                  </Box>
                </div>


              </Box>
            </Box>}

            {/* Content displayed below the diagram */}
            {(diagram.descriptionAfter || (diagram.keySteps && diagram.keySteps.length > 0)) && (
              <Box
                data-content="after-diagram"
                sx={{
                  mt: 2,
                  backgroundColor: 'transparent',
                  overflow: 'hidden',
                  minHeight: 'auto',
                  height: 'auto'
                }}
              >
                {diagram.descriptionAfter && (
                  <Box
                    data-description="after"
                    sx={{
                      p: 1,
                      pl: 0,
                      fontStyle: 'normal',
                      mb: 2,
                      minHeight: 'auto',
                      height: 'auto'
                    }}
                  >
                    <Box sx={{ fontSize: "14px", '& p': { mb: 0.5 }, '& ul, & ol': { mt: 0, mb: 0.5, pl: 2 }, '& li': { mb: 0.25 } }}>
                      <MarkdownRenderer content={diagram.descriptionAfter} />
                    </Box>
                  </Box>
                )}

                {diagram.keySteps && diagram.keySteps.length > 0 && (
                  <Box
                    data-content="key-steps"
                    sx={{
                      p: 1,
                      pl: 0,
                      minHeight: 'auto',
                      height: 'auto'
                    }}
                  >
                    <Box component="span" sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: "14px"
                    }}>
                      Key Steps
                    </Box>
                    <Box component="ul" sx={{
                      pl: 2,
                      my: 0,
                      '& li': {
                        mb: 0.5
                      }
                    }}>
                      {diagram.keySteps.map((step, stepIndex) => (
                        <Box component="li" key={stepIndex}>
                          <Box component="span" sx={{ fontSize: "14px" }}>
                            {step}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Inline improve input */}
            {showImproveInput === diagramId && (
              <InlineImproveInput
                improvePrompt={improvePrompt}
                onPromptChange={onImprovePromptChange}
                onSend={onSendImprove}
                onCancel={onCancelImprove}
                promptText="How would you like to improve this diagram?"
                placeholder="Describe the improvements you'd like..."
              />
            )}

          </Box>

          {/* Action buttons */}
          <Box sx={{
            position: 'absolute',
            right: 10,
            top: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            zIndex: 20
          }}>
            <ActionButtons
              item={diagram}
              itemIndex={index}
              onEdit={onEdit}
              onImprove={onImprove}
              onExplain={onExplain}
              onCopy={onCopy}
              onDelete={onDelete}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              showZoomControls={true}
              zoomLevel={zoomLevel}
              minZoom={0.5}
              maxZoom={3.0}
              vertical={true}
              // onExpandHeight={() => onToggleHeight(diagram.id)}
              isHeightExpanded={isHeightExpanded}
              onOpenPopup={() => onOpenPopup(diagram)}
              onComment={onComment}
              commentCount={commentCount}
              isApproved={isValidated}
              approvedByType={memberType}
            />
          </Box>
        </>
      }
    />
  );
};

export default ProcessFlowDiagramItem;
